import { useTreatments, SplitContext } from '@splitsoftware/splitio-react';
import { useContext, useEffect } from 'react';

import {
  isSessionLockedFlag,
  getLockedFlagValue,
  lockFlagValue
} from '~/common/helpers/sessionLockedFlags';
import WebAnalytics from '~/common/helpers/webAnalytics';
import { SplitAttributesContext } from '~/scenes/Others/scenes/App/components/SplitProvider';

const useFeatureFlag = (
  names: string[],
  attributes?: SplitIO.Attributes | undefined,
  key?: SplitIO.SplitKey | undefined,
  eventDisabled = false
) => {
  const splitContext = useContext(SplitContext);
  const splitAttributesContext = useContext(SplitAttributesContext);
  const splitKey = key || splitAttributesContext.key.toString();

  const evaluationAttributes = {
    ...splitAttributesContext,
    ...attributes
  };

  const treatments = useTreatments(names, evaluationAttributes, splitKey);

  const targetingAttributesReady =
    splitContext.isReady &&
    Boolean(evaluationAttributes.realm) &&
    Boolean(evaluationAttributes.cluster);

  const lockedTreatments = { ...treatments };
  names.forEach(name => {
    if (isSessionLockedFlag(name)) {
      const lockedValue = getLockedFlagValue(name);

      if (lockedValue !== null) {
        lockedTreatments[name] = {
          ...lockedTreatments[name],
          treatment: lockedValue
        };
      } else if (
        targetingAttributesReady &&
        treatments[name] &&
        treatments[name].treatment !== 'control'
      ) {
        lockFlagValue(name, treatments[name].treatment);
      }
    }
  });
  const sendToFullStory = (name: string) => {
    const treatment = lockedTreatments[name];

    if (treatment) {
      return WebAnalytics.sendEvent('[Global] (Feature Flag) Evaluation', {
        feature: name,
        keyName: splitKey,
        treatment: treatment.treatment,
        time: new Date().getTime(),
        treatmentConfig: treatment.config,
        attributes: {
          ...splitAttributesContext,
          ...attributes
        }
      });
    }
    return null;
  };

  useEffect(() => {
    if (!eventDisabled && splitContext.isReady) {
      names.forEach(sendToFullStory);
    }
  }, [eventDisabled, splitContext.isReady]);

  useEffect(() => {
    if (splitContext.isReady && splitContext.client) {
      (window as any).splitClient = splitContext.client;
    }
  }, [splitContext.isReady, splitContext.client]);

  return {
    ...splitContext,
    treatments: lockedTreatments
  };
};

export default useFeatureFlag;
